import { useState, useMemo, useRef, useEffect, Fragment } from "react";
import {
  Users,
  ArrowUpDown,
  ChevronDown,
  ChevronUp,
} from "../../../../components/icons";
import type { PlayerStats } from "../../hooks/usePlayerStats";
import PlayerAvatar from "../../../../components/PlayerAvatar";
import { formatDisplayName } from "../../../../lib/nameFormat";
import type { RatingBreakdown } from "../../../../types/playerStats";

type SortField = keyof Pick<
  PlayerStats,
  | "jerseyNumber"
  | "totalEvents"
  | "passesGood"
  | "passesBad"
  | "passesReceived"
  | "shots"
  | "shotsOnTarget"
  | "goals"
  | "penalties"
  | "penaltyGoals"
  | "assists"
  | "duelsWon"
  | "duelsLost"
  | "foulsCommitted"
  | "foulsReceived"
  | "interceptions"
  | "recoveries"
  | "clearances"
  | "blocks"
  | "saves"
  | "yellowCards"
  | "redCards"
>;

type TeamFilter = "all" | "home" | "away";

interface ColumnDef {
  key: SortField;
  label: string;
  abbr: string;
  group: string;
}

const COLUMNS: ColumnDef[] = [
  { key: "jerseyNumber", label: "Jersey", abbr: "#", group: "info" },
  { key: "totalEvents", label: "Events", abbr: "EVT", group: "general" },
  { key: "passesGood", label: "Good Passes", abbr: "PG", group: "passing" },
  { key: "passesBad", label: "Bad Passes", abbr: "PB", group: "passing" },
  {
    key: "passesReceived",
    label: "Passes Received",
    abbr: "PR",
    group: "passing",
  },
  { key: "shots", label: "Shots", abbr: "SH", group: "shooting" },
  {
    key: "shotsOnTarget",
    label: "Shots on Target",
    abbr: "SOT",
    group: "shooting",
  },
  { key: "goals", label: "Goals", abbr: "G", group: "shooting" },
  { key: "penalties", label: "Penalties", abbr: "PEN", group: "shooting" },
  {
    key: "penaltyGoals",
    label: "Penalty Goals",
    abbr: "PGO",
    group: "shooting",
  },
  { key: "assists", label: "Assists", abbr: "AST", group: "passing" },
  { key: "duelsWon", label: "Duels Won", abbr: "DW", group: "duels" },
  { key: "duelsLost", label: "Duels Lost", abbr: "DL", group: "duels" },
  {
    key: "foulsCommitted",
    label: "Fouls Done",
    abbr: "FC",
    group: "discipline",
  },
  {
    key: "foulsReceived",
    label: "Fouls Received",
    abbr: "FR",
    group: "discipline",
  },
  {
    key: "interceptions",
    label: "Interceptions",
    abbr: "INT",
    group: "defensive",
  },
  { key: "recoveries", label: "Recoveries", abbr: "REC", group: "defensive" },
  { key: "clearances", label: "Clearances", abbr: "CLR", group: "defensive" },
  { key: "blocks", label: "Blocks", abbr: "BLK", group: "defensive" },
  // PMA-COCK-177 (goalkeeper saves become a counted stat, user decision
  // 2026-09-09): see usePlayerStats.ts for the credit rule.
  { key: "saves", label: "Saves", abbr: "SV", group: "defensive" },
  {
    key: "yellowCards",
    label: "Yellow Cards",
    abbr: "YC",
    group: "discipline",
  },
  { key: "redCards", label: "Red Cards", abbr: "RC", group: "discipline" },
];

interface AdvancedPlayerEntry {
  xg: number;
  progressive_passes: number;
  progressive_carries: number;
}

/**
 * EPIC-07 — Per-Player Match Score (Rating Engine). Narrow, merge-friendly
 * shape produced by `usePlayerRatings` (fetch-and-merge by player_id from
 * the backend's `GET /dashboard/matches/{id}/player-stats`). Mirrors
 * `AdvancedPlayerEntry`'s ad-hoc-local-shape convention above — the rating
 * has exactly ONE source (the backend); this table never re-derives it.
 */
export interface PlayerRatingEntry {
  rating: number | null;
  rating_provisional: boolean;
  rating_breakdown: RatingBreakdown | null;
}

/** goalkeeper | defense | midfield | attack | other, per the backend's
 * PositionGroup enum. Falls back to the raw string for an unknown value. */
const POSITION_GROUP_LABEL_KEYS: Record<string, string> = {
  goalkeeper: "playerStats.positionGroupGoalkeeper",
  defense: "playerStats.positionGroupDefense",
  midfield: "playerStats.positionGroupMidfield",
  attack: "playerStats.positionGroupAttack",
  other: "playerStats.positionGroupOther",
};

/** Judge round 1, P4: title-casing the raw backend key left the breakdown
 * English-only under a Spanish UI ("Pass Completed" instead of "Pase
 * Completado"). Real i18n lookup keyed by the literal backend key
 * (`playerStats.contributionKeys.<key>`, both `en`/`es` `logger.json`) —
 * `prettifyContributionKey` now serves only as the visible fallback for a
 * key not yet in the locale files (kept in sync with
 * `app/services/player_rating.py`'s `RATING_WEIGHTS`, 25 keys as of the
 * judge round 1 weight-table expansion). */
function prettifyContributionKey(key: string): string {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function contributionKeyLabel(
  t: (key: string, fallback: string) => string,
  key: string,
): string {
  return t(`playerStats.contributionKeys.${key}`, prettifyContributionKey(key));
}

interface PlayerStatsTableProps {
  stats: PlayerStats[];
  homeTeamName: string;
  awayTeamName: string;
  t: (key: string, fallback: string) => string;
  advancedPlayerStats?: Record<string, AdvancedPlayerEntry>;
  /** EPIC-07: undefined until `usePlayerRatings` has engaged at all (no
   * matchId) — when the caller passes any of these three, the RTG column
   * renders; a missing/failed entry for a given player renders as
   * "unavailable", never as a silently-zeroed rating.
   *
   * BUG FIXED HERE (found live by e2e/logger-rating.spec.ts's real fetch-
   * failure test, fe-epic07/T3): once a fetch genuinely fails,
   * `ratingLoading` correctly flips back to `false` and `ratingByPlayerId`
   * stays `undefined` — a column-visibility check of ONLY those two
   * (`ratingByPlayerId !== undefined || ratingLoading`) therefore goes
   * false the moment the error settles, and the WHOLE column vanishes
   * instead of showing the unavailable dash. `ratingError` closes that
   * gap. The pre-fix unit test for this case passed `ratingByPlayerId={{}}`
   * (a valid empty response), which is `!== undefined` and never exercised
   * this branch — see the new test covering the real `undefined` +
   * `ratingError` shape `usePlayerRatings` actually returns on failure. */
  ratingByPlayerId?: Record<string, PlayerRatingEntry>;
  ratingLoading?: boolean;
  ratingError?: boolean;
}

export function PlayerStatsTable({
  stats,
  homeTeamName,
  awayTeamName,
  t,
  advancedPlayerStats,
  ratingByPlayerId,
  ratingLoading,
  ratingError,
}: PlayerStatsTableProps) {
  const [sortField, setSortField] = useState<SortField>("totalEvents");
  const [sortAsc, setSortAsc] = useState(false);
  const [teamFilter, setTeamFilter] = useState<TeamFilter>("all");
  const scrollRef = useRef<HTMLDivElement>(null);
  const [moreRight, setMoreRight] = useState(false);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () =>
      setMoreRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
    update();
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [teamFilter, stats.length]);
  // EPIC-07: which player's rating breakdown row is expanded, if any.
  const [expandedRatingPlayerId, setExpandedRatingPlayerId] = useState<
    string | null
  >(null);
  const showRatingColumn =
    ratingByPlayerId !== undefined ||
    Boolean(ratingLoading) ||
    Boolean(ratingError);

  const filtered = useMemo(() => {
    let rows = stats;
    if (teamFilter !== "all") {
      rows = rows.filter((r) => r.teamSide === teamFilter);
    }
    return [...rows].sort((a, b) => {
      const av = a[sortField];
      const bv = b[sortField];
      const diff = (av as number) - (bv as number);
      return sortAsc ? diff : -diff;
    });
  }, [stats, sortField, sortAsc, teamFilter]);

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortAsc((prev) => !prev);
    } else {
      setSortField(field);
      setSortAsc(false);
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (field !== sortField) {
      return <ArrowUpDown className="w-3 h-3 opacity-30" />;
    }
    return sortAsc ? (
      <ChevronUp className="w-3 h-3" />
    ) : (
      <ChevronDown className="w-3 h-3" />
    );
  };

  if (stats.length === 0) {
    return (
      <div
        className="bg-slate-900 border border-slate-700/60 rounded-lg shadow-lg p-6 text-center text-slate-400"
        data-testid="player-stats-table"
      >
        <Users className="w-8 h-8 mx-auto mb-2 opacity-40" />
        <p>{t("analytics.noPlayerStats", "No player events recorded yet")}</p>
      </div>
    );
  }

  return (
    <div
      className="bg-slate-900 border border-slate-700/60 rounded-lg shadow-lg p-4 sm:p-6"
      data-testid="player-stats-table"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2">
        <h3 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
          <Users className="text-indigo-400" />
          {t("analytics.playerStats", "Player Statistics")}
        </h3>

        {/* Team filter buttons */}
        <div
          className="flex gap-1 rounded-lg bg-slate-800 p-1"
          data-testid="player-stats-team-filter"
        >
          {(
            [
              { key: "all", label: t("analytics.allTeams", "All") },
              { key: "home", label: homeTeamName },
              { key: "away", label: awayTeamName },
            ] as const
          ).map((opt) => (
            <button
              key={opt.key}
              onClick={() => setTeamFilter(opt.key as TeamFilter)}
              data-testid={`player-stats-filter-${opt.key}`}
              className={`min-h-11 min-w-11 px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                teamFilter === opt.key
                  ? "bg-indigo-600 text-white shadow-sm"
                  : "text-slate-300 hover:bg-slate-700"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Scrollable table */}
      <div className="relative">
        <div
          ref={scrollRef}
          className="overflow-x-auto -mx-4 sm:-mx-6"
          data-testid="player-stats-scroll"
        >
          <div className="min-w-[900px] px-4 sm:px-6">
            <table className="w-full text-sm" data-testid="player-stats-grid">
              <thead>
                <tr className="border-b-2 border-slate-700">
                  {/* Player name column */}
                  <th className="text-left py-2 px-2 font-semibold text-slate-200 sticky left-0 bg-slate-900 z-10 min-w-[180px]">
                    {t("analytics.player", "Player")}
                  </th>
                  {COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      className="py-2 px-1 text-center cursor-pointer select-none hover:bg-slate-800 transition-colors"
                      title={col.label}
                      onClick={() => handleSort(col.key)}
                      data-testid={`player-stats-col-${col.abbr.replace(
                        /[^a-zA-Z0-9]/g,
                        "",
                      )}`}
                    >
                      <div className="flex items-center justify-center gap-0.5">
                        <span className="font-semibold text-slate-300 text-xs">
                          {col.abbr}
                        </span>
                        <SortIcon field={col.key} />
                      </div>
                    </th>
                  ))}
                  {advancedPlayerStats && (
                    <>
                      <th
                        className="py-2 px-1 text-center select-none"
                        title={t("advancedMetrics.xgFull", "Expected Goals")}
                        data-testid="player-stats-col-xG"
                      >
                        <span className="font-semibold text-indigo-300 text-xs">
                          xG
                        </span>
                      </th>
                      <th
                        className="py-2 px-1 text-center select-none"
                        title={t(
                          "advancedMetrics.progressivePassesFull",
                          "Progressive Passes",
                        )}
                        data-testid="player-stats-col-PP"
                      >
                        <span className="font-semibold text-indigo-300 text-xs">
                          PP
                        </span>
                      </th>
                      <th
                        className="py-2 px-1 text-center select-none"
                        title={t(
                          "advancedMetrics.progressiveCarriesFull",
                          "Progressive Carries",
                        )}
                        data-testid="player-stats-col-PC"
                      >
                        <span className="font-semibold text-indigo-300 text-xs">
                          PgC
                        </span>
                      </th>
                    </>
                  )}
                  {showRatingColumn && (
                    <th
                      className="py-2 px-1 text-center select-none"
                      title={t("advancedMetrics.ratingFull", "Match Rating")}
                      data-testid="player-stats-col-RTG"
                    >
                      <span className="font-semibold text-amber-400 text-xs">
                        RTG
                      </span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {filtered.map((player) => {
                  const adv = advancedPlayerStats?.[player.playerId];
                  const ratingEntry = ratingByPlayerId?.[player.playerId];
                  const rating = ratingEntry?.rating ?? null;
                  const isRatingExpanded =
                    expandedRatingPlayerId === player.playerId;
                  return (
                    <Fragment key={player.playerId}>
                      <tr
                        className="border-b border-slate-800 hover:bg-slate-800/60 transition-colors"
                        data-testid={`player-stats-row-${player.playerId}`}
                      >
                        {/* Player name cell */}
                        <td className="py-2 px-2 sticky left-0 bg-slate-900 z-10 min-w-[180px]">
                          <div className="flex items-center gap-1.5">
                            <PlayerAvatar
                              player={{
                                full_name: formatDisplayName(player.playerName),
                                photo_url: player.photoUrl,
                                photo_image: player.photoImage,
                              }}
                              size={22}
                            />
                            <span
                              className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold text-white ${
                                player.teamSide === "home"
                                  ? "bg-emerald-500"
                                  : "bg-blue-500"
                              }`}
                            >
                              {player.jerseyNumber}
                            </span>
                            <span
                              className="font-medium text-slate-100 truncate max-w-[130px]"
                              title={formatDisplayName(player.playerName)}
                            >
                              {formatDisplayName(player.playerName)}
                            </span>
                          </div>
                        </td>
                        {COLUMNS.map((col) => {
                          const val = player[col.key];
                          const isZero = val === 0;
                          return (
                            <td
                              key={col.key}
                              className={`py-2 px-1 text-center tabular-nums ${
                                isZero
                                  ? "text-slate-400"
                                  : "text-slate-100 font-medium"
                              }`}
                              data-testid={`ps-${
                                player.playerId
                              }-${col.abbr.replace(/[^a-zA-Z0-9]/g, "")}`}
                            >
                              {isZero ? "–" : val}
                            </td>
                          );
                        })}
                        {advancedPlayerStats && (
                          <>
                            <td
                              className={`py-2 px-1 text-center tabular-nums ${
                                !adv || adv.xg === 0
                                  ? "text-slate-400"
                                  : "text-indigo-300 font-medium"
                              }`}
                              data-testid={`ps-${player.playerId}-xG`}
                            >
                              {adv ? adv.xg.toFixed(2) : "–"}
                            </td>
                            <td
                              className={`py-2 px-1 text-center tabular-nums ${
                                !adv || adv.progressive_passes === 0
                                  ? "text-slate-400"
                                  : "text-indigo-300 font-medium"
                              }`}
                              data-testid={`ps-${player.playerId}-PP`}
                            >
                              {adv
                                ? adv.progressive_passes === 0
                                  ? "–"
                                  : adv.progressive_passes
                                : "–"}
                            </td>
                            <td
                              className={`py-2 px-1 text-center tabular-nums ${
                                !adv || adv.progressive_carries === 0
                                  ? "text-slate-400"
                                  : "text-indigo-300 font-medium"
                              }`}
                              data-testid={`ps-${player.playerId}-PC`}
                            >
                              {adv
                                ? adv.progressive_carries === 0
                                  ? "–"
                                  : adv.progressive_carries
                                : "–"}
                            </td>
                          </>
                        )}
                        {showRatingColumn && (
                          <td
                            className="py-2 px-1 text-center tabular-nums"
                            data-testid={`ps-${player.playerId}-RTG`}
                          >
                            {rating === null ? (
                              <span
                                className="text-slate-400"
                                title={
                                  ratingLoading && !ratingByPlayerId
                                    ? undefined
                                    : t(
                                        "playerStats.ratingUnavailableHint",
                                        "Rating not available for this player yet.",
                                      )
                                }
                              >
                                {ratingLoading && !ratingByPlayerId ? "…" : "–"}
                              </span>
                            ) : (
                              <button
                                type="button"
                                onClick={() =>
                                  setExpandedRatingPlayerId(
                                    isRatingExpanded ? null : player.playerId,
                                  )
                                }
                                aria-expanded={isRatingExpanded}
                                disabled={!ratingEntry?.rating_breakdown}
                                title={t(
                                  "playerStats.ratingBreakdownToggle",
                                  "View rating breakdown",
                                )}
                                data-testid={`ps-rating-toggle-${player.playerId}`}
                                className="inline-flex items-center gap-1 font-semibold text-amber-300 hover:text-amber-200 disabled:cursor-default disabled:hover:text-amber-300"
                              >
                                {rating.toFixed(1)}
                                {ratingEntry?.rating_provisional && (
                                  <span
                                    className="text-[9px] font-bold text-amber-400"
                                    data-testid={`ps-rating-provisional-${player.playerId}`}
                                    title={t(
                                      "playerStats.ratingProvisionalHint",
                                      "Provisional — fewer than 15 minutes played.",
                                    )}
                                  >
                                    P
                                  </span>
                                )}
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                      {isRatingExpanded && ratingEntry?.rating_breakdown && (
                        <tr
                          className="bg-slate-800/40"
                          data-testid={`ps-rating-breakdown-row-${player.playerId}`}
                        >
                          <td
                            colSpan={
                              1 +
                              COLUMNS.length +
                              (advancedPlayerStats ? 3 : 0) +
                              (showRatingColumn ? 1 : 0)
                            }
                            className="py-3 px-3"
                          >
                            <div
                              className="space-y-2 text-xs text-slate-300"
                              data-testid={`ps-rating-breakdown-${player.playerId}`}
                            >
                              <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                <div>
                                  <dt className="uppercase tracking-wide text-[10px] text-slate-400">
                                    {t(
                                      "playerStats.ratingBaseline",
                                      "Baseline",
                                    )}
                                  </dt>
                                  <dd className="font-semibold text-slate-100">
                                    {ratingEntry.rating_breakdown.baseline.toFixed(
                                      1,
                                    )}
                                  </dd>
                                </div>
                                <div>
                                  <dt className="uppercase tracking-wide text-[10px] text-slate-400">
                                    {t(
                                      "playerStats.ratingAdjustment",
                                      "Adjustment",
                                    )}
                                  </dt>
                                  <dd className="font-semibold text-slate-100">
                                    {ratingEntry.rating_breakdown
                                      .total_adjustment >= 0
                                      ? "+"
                                      : ""}
                                    {ratingEntry.rating_breakdown.total_adjustment.toFixed(
                                      2,
                                    )}
                                  </dd>
                                </div>
                                <div>
                                  <dt className="uppercase tracking-wide text-[10px] text-slate-400">
                                    {t(
                                      "playerStats.ratingPositionGroup",
                                      "Position Group",
                                    )}
                                  </dt>
                                  <dd className="font-semibold text-slate-100">
                                    {t(
                                      POSITION_GROUP_LABEL_KEYS[
                                        ratingEntry.rating_breakdown
                                          .position_group
                                      ] ?? "playerStats.positionGroupOther",
                                      ratingEntry.rating_breakdown
                                        .position_group,
                                    )}
                                  </dd>
                                </div>
                                <div>
                                  <dt
                                    className="uppercase tracking-wide text-[10px] text-slate-400 cursor-help"
                                    title={t(
                                      "playerStats.ratingGateMinutesHint",
                                      "Minutes used to compute this rating — differs from Minutes when a red card ends the player's involvement early (Minutes still counts the whole match).",
                                    )}
                                  >
                                    {t(
                                      "playerStats.ratingGateMinutes",
                                      "Rated Minutes",
                                    )}
                                  </dt>
                                  <dd className="font-semibold text-slate-100">
                                    {ratingEntry.rating_breakdown.gate_minutes.toFixed(
                                      0,
                                    )}
                                    &apos;
                                  </dd>
                                </div>
                              </dl>
                              {ratingEntry.rating_breakdown.clamped && (
                                <p
                                  className="text-amber-400"
                                  data-testid={`ps-rating-breakdown-${player.playerId}-clamped`}
                                >
                                  {t(
                                    "playerStats.ratingClampedNote",
                                    "Rating adjusted to fit the 0–10 scale.",
                                  )}
                                </p>
                              )}
                              {ratingEntry.rating_breakdown.contributions
                                .length > 0 ? (
                                <ul className="divide-y divide-slate-700">
                                  {ratingEntry.rating_breakdown.contributions.map(
                                    (c) => (
                                      <li
                                        key={c.key}
                                        className="flex items-center justify-between py-1"
                                        data-testid={`ps-rating-breakdown-${player.playerId}-contribution-${c.key}`}
                                      >
                                        <span>
                                          {contributionKeyLabel(t, c.key)} ×{" "}
                                          {c.count}
                                        </span>
                                        <span
                                          className={
                                            c.points >= 0
                                              ? "text-emerald-400 font-medium"
                                              : "text-rose-400 font-medium"
                                          }
                                        >
                                          {c.points >= 0 ? "+" : ""}
                                          {c.points.toFixed(2)}
                                        </span>
                                      </li>
                                    ),
                                  )}
                                </ul>
                              ) : (
                                <p className="text-slate-400">
                                  {t(
                                    "playerStats.ratingNoContributions",
                                    "No scoring events recorded yet.",
                                  )}
                                </p>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        {moreRight && (
          <div
            aria-hidden="true"
            data-testid="player-stats-scroll-fade"
            className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-slate-900 to-transparent"
          />
        )}
      </div>

      {/* Legend */}
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-slate-400 uppercase tracking-wide">
        <span>PG = {t("analytics.goodPasses", "Good Passes")}</span>
        <span>PB = {t("analytics.badPasses", "Bad Passes")}</span>
        <span>PR = {t("analytics.passesReceived", "Passes Recv")}</span>
        <span>SH = {t("analytics.shots", "Shots")}</span>
        <span>SOT = {t("analytics.shotsOnTarget", "On Target")}</span>
        <span>G = {t("analytics.goals", "Goals")}</span>
        <span>PEN = {t("analytics.penalties", "Penalties")}</span>
        <span>PGO = {t("analytics.penaltyGoals", "Penalty Goals")}</span>
        <span>AST = {t("analytics.assists", "Assists")}</span>
        <span>DW = {t("analytics.duelsWon", "Duels Won")}</span>
        <span>DL = {t("analytics.duelsLost", "Duels Lost")}</span>
        <span>FC = {t("analytics.foulsCommitted", "Fouls Done")}</span>
        <span>FR = {t("analytics.foulsReceived", "Fouls Recv")}</span>
        <span>INT = {t("analytics.interceptions", "Interceptions")}</span>
        <span>REC = {t("analytics.recoveries", "Recoveries")}</span>
        <span>CLR = {t("analytics.clearances", "Clearances")}</span>
        <span>BLK = {t("analytics.blocks", "Blocks")}</span>
        <span>SV = {t("analytics.saves", "Saves")}</span>
        <span>YC = {t("analytics.yellowCards", "Yellow Cards")}</span>
        <span>RC = {t("analytics.redCards", "Red Cards")}</span>
      </div>
    </div>
  );
}
